import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebaseConfig';
import { useTenantResolution } from '../hooks/useTenantResolution';
import AuthRecoveryPanel from './AuthRecoveryPanel';
import WorkspaceAccessEnded from './WorkspaceAccessEnded';
import {apiFetch} from '../utils/apiBase';
import {ACCOUNT_STATES, fetchAccountState} from '../utils/accountLifecycle';
import {
  AUTH_RECOVERY_TIMEOUT_MS,
  getSafeErrorCategory,
  getSafeFirebaseErrorCode,
  isAuthRecoveryTimeout,
  logAuthDiagnostic,
  resetLocalAuthSession,
  withAuthRecoveryTimeout,
} from '../utils/authRecovery';
import {ConversionProvider} from '../features/conversion/ConversionContext';
import FreeStatusStrip from '../features/conversion/FreeStatusStrip';
import UpgradeReturnNotice from '../features/conversion/UpgradeReturnNotice';

const DEFAULT_FALLBACK_REDIRECT = '/';

export default function AuthGate({ children, redirectTo }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(() => auth.currentUser);
  const [userDocLoading, setUserDocLoading] = useState(false);
  const [userDocData, setUserDocData] = useState(null);
  const [accessRecovery, setAccessRecovery] = useState(null);
  const [isResettingLocalSession, setIsResettingLocalSession] = useState(false);
  const [lifecycleState, setLifecycleState] = useState(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [accountState, setAccountState] = useState(null);
  const [accountStateLoading, setAccountStateLoading] = useState(false);
  const { tenantId, membershipLoading, membershipTimedOut } = useTenantResolution({
    currentUser: user,
    userDocData,
    userDocLoading,
  });

  useEffect(() => {
    logAuthDiagnostic('auth.init.start', { surface: 'auth_gate' });
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setIsLoading(false);
      logAuthDiagnostic('auth.init.end', {
        surface: 'auth_gate',
        hasUser: Boolean(nextUser),
      });
      setAccessRecovery((prev) => (prev?.source === 'auth_gate_init_timeout' ? null : prev));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isLoading) return undefined;
    const timeoutId = setTimeout(() => {
      logAuthDiagnostic('auth.init.timeout', { surface: 'auth_gate' });
      setAccessRecovery({ source: 'auth_gate_init_timeout', shownAt: Date.now() });
    }, AUTH_RECOVERY_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  useEffect(() => {
    let isMounted = true;
    if (!user?.uid) {
      setUserDocData(null);
      setUserDocLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setUserDocLoading(true);
    setAccessRecovery((prev) => (prev?.source === 'auth_gate_user_doc_timeout' ? null : prev));
    (async () => {
      try {
        logAuthDiagnostic('user_document.load.start', { surface: 'auth_gate' });
        const snapshot = await withAuthRecoveryTimeout(
          getDoc(doc(db, 'users', user.uid)),
          'auth_gate.user_document.get_doc',
        );
        if (!isMounted) return;
        setUserDocData(snapshot.exists() ? snapshot.data() : null);
        logAuthDiagnostic('user_document.load.success', { surface: 'auth_gate' });
      } catch (error) {
        logAuthDiagnostic(
          isAuthRecoveryTimeout(error) ? 'user_document.load.timeout' : 'user_document.load.failure',
          {
            surface: 'auth_gate',
            code: getSafeFirebaseErrorCode(error),
            category: getSafeErrorCategory(error),
          },
        );
        console.error('auth_gate_user_profile_load_failed', {
          errorCategory: 'user_profile_load_failed',
        });
        if (!isMounted) return;
        setUserDocData(null);
        if (isAuthRecoveryTimeout(error)) {
          setAccessRecovery({ source: 'auth_gate_user_doc_timeout', shownAt: Date.now() });
        }
      } finally {
        if (isMounted) {
          setUserDocLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [user?.uid]);

  useEffect(() => {
    let active = true;
    if (!user?.uid) {
      setAccountState(null);
      setAccountStateLoading(false);
      return () => { active = false; };
    }
    setAccountStateLoading(true);
    fetchAccountState()
      .then((result) => {
        if (active) setAccountState(result?.accountState || null);
      })
      .catch((error) => {
        logAuthDiagnostic('account_state.failure', {
          surface: 'auth_gate',
          code: getSafeFirebaseErrorCode(error),
          category: getSafeErrorCategory(error),
        });
        if (active) {
          setAccountState(null);
          setAccessRecovery({source: 'auth_gate_account_state_failure', shownAt: Date.now()});
        }
      })
      .finally(() => {
        if (active) setAccountStateLoading(false);
      });
    return () => { active = false; };
  }, [user?.uid]);

  const isAuthRoute = location.pathname.startsWith('/auth');
  const redirectTarget =
    redirectTo ??
    (isAuthRoute ? DEFAULT_FALLBACK_REDIRECT : `${location.pathname}${location.search}`);
  const isAccessLoading =
    isLoading || userDocLoading || accountStateLoading || membershipLoading || lifecycleLoading;

  useEffect(() => {
    if (!membershipTimedOut) return;
    setAccessRecovery({ source: 'auth_gate_membership_timeout', shownAt: Date.now() });
  }, [membershipTimedOut]);

  useEffect(() => {
    let active = true;
    if (!user?.uid || accountState !== ACCOUNT_STATES.READY || !tenantId) {
      setLifecycleState(null);
      setLifecycleLoading(false);
      return () => { active = false; };
    }
    setLifecycleLoading(true);
    apiFetch(`/api/lifecycle/status?companyId=${encodeURIComponent(tenantId)}`, {
      method: 'GET',
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (active && response.ok) setLifecycleState(data);
      })
      .catch(() => {
        if (active) setLifecycleState(null);
      })
      .finally(() => {
        if (active) setLifecycleLoading(false);
      });
    return () => { active = false; };
  }, [accountState, tenantId, user?.uid]);

  useEffect(() => {
    if (isAuthRoute || !isAccessLoading) {
      setAccessRecovery((prev) =>
        prev?.source === 'auth_gate_access_timeout' ? null : prev,
      );
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      logAuthDiagnostic('protected_route.access.timeout', { surface: 'auth_gate' });
      setAccessRecovery({ source: 'auth_gate_access_timeout', shownAt: Date.now() });
    }, AUTH_RECOVERY_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [isAccessLoading, isAuthRoute]);

  const handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  const handleResetLocalSession = async () => {
    if (isResettingLocalSession) return;
    setIsResettingLocalSession(true);
    try {
      await resetLocalAuthSession({ auth, signOutFn: signOut });
      setUser(null);
      setUserDocData(null);
      setUserDocLoading(false);
      setAccessRecovery(null);
      navigate(`/auth?redirectTo=${encodeURIComponent(redirectTarget)}`, { replace: true });
    } catch (error) {
      logAuthDiagnostic('reset_local_session.failure', {
        surface: 'auth_gate',
        code: getSafeFirebaseErrorCode(error),
        category: getSafeErrorCategory(error),
      });
      setAccessRecovery({ source: 'auth_gate_reset_failed', shownAt: Date.now() });
    } finally {
      setIsResettingLocalSession(false);
    }
  };

  if (isAuthRoute) {
    return <>{children}</>;
  }

  if (accessRecovery) {
    return (
      <div className="flex min-h-screen items-start justify-center bg-slate-50 px-4 py-8 sm:items-center">
        <div className="w-full max-w-sm">
          <AuthRecoveryPanel
            isResetting={isResettingLocalSession}
            onReload={handleReload}
            onResetLocalSession={handleResetLocalSession}
          />
        </div>
      </div>
    );
  }

  if (isAccessLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white/95 p-6 text-center shadow-lg">
          <p className="text-sm font-semibold text-slate-700">Checking access…</p>
        </div>
      </div>
    );
  }

  if (lifecycleState?.accessEnded) {
    return <WorkspaceAccessEnded lifecycle={lifecycleState} />;
  }

  if (!user) {
    return <Navigate to={`/auth?redirectTo=${encodeURIComponent(redirectTarget)}`} replace />;
  }

  if (accountState === ACCOUNT_STATES.NEW) {
    return <Navigate to={`/auth?mode=sign_up&redirectTo=${encodeURIComponent(redirectTarget)}`} replace />;
  }

  if (accountState === ACCOUNT_STATES.LEGAL_UPDATE_REQUIRED) {
    return <Navigate to={`/auth?redirectTo=${encodeURIComponent(redirectTarget)}`} replace />;
  }

  if (accountState === ACCOUNT_STATES.DISABLED) {
    return <Navigate to={`/auth?redirectTo=${encodeURIComponent(redirectTarget)}`} replace />;
  }

  const isDisabled = userDocData?.status === 'disabled';
  const isSuperAdmin = userDocData?.role === 'super_admin';
  const hasTenantId = typeof tenantId === 'string' && tenantId.trim().length > 0;
  const isActive =
    userDocData?.status === 'active' || (!userDocData?.status && hasTenantId) || isSuperAdmin;

  if (isDisabled) {
    return <Navigate to={`/auth?redirectTo=${encodeURIComponent(redirectTarget)}`} replace />;
  }

  if (!isSuperAdmin && !isActive) {
    return <Navigate to={`/auth?redirectTo=${encodeURIComponent(redirectTarget)}`} replace />;
  }

  const isWorkspaceProductRoute = location.pathname.startsWith('/instance/');
  return (
    <ConversionProvider companyId={tenantId} userId={user.uid}>
      {isWorkspaceProductRoute ? (
        <FreeStatusStrip companyId={tenantId} userId={user.uid} />
      ) : null}
      {isWorkspaceProductRoute ? <UpgradeReturnNotice /> : null}
      {children}
    </ConversionProvider>
  );
}
